import { useEffect, useMemo, useState } from 'react'

import { Music, Play, Square } from 'lucide-react'
import { DateTime } from 'luxon'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Song } from '@/core/api/entities/song/song'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePlaylistSample } from '@/core/hooks/usePlaylist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Link } from '@/shared/components/atoms/Link'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/shared/components/molecules/AlertDialog'
import Player from '@/shared/components/organisms/Player/Player'
import { formatDuration } from '@/shared/utils/helpers'

import { EventDeleteForm } from './EventDeleteForm'
import { CalendarEvent } from '../../context/Data'
import { useEvents } from '../../context/EventContext'
import { usePlaylistData } from '../../hooks/usePlaylistData'

interface EventViewProps {
	event?: CalendarEvent
	readOnly?: boolean
}

export const EventView = ({ event, readOnly = false }: EventViewProps) => {
	const { t } = useTranslation()
	const {
		eventViewOpen,
		setEventViewOpen,
		setEventEditOpen,
		setWeatherSlotStepperOpen,
		setOccupancySlotStepperOpen,
		setSelectedSmartType,
	} = useEvents()

	const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({})

	// Create stub playlists from event's playlist IDs
	const selectedPlaylists = useMemo(() => {
		if (!event?.playlists) return []
		return event.playlists.map(
			(p) =>
				({
					id: p.id,
					title: t('entities.schedule.event_view.playlist_stub', {
						id: p.id,
					}), // Stub title - will be replaced by real data
					percentage: p.percentage,
				}) as Playlist
		)
	}, [event?.playlists, t])

	// Fetch full playlist data
	const { allPlaylists } = usePlaylistData({
		dialogOpen: eventViewOpen,
		selectedPlaylists,
	})

	// Get full playlist data for display
	const displayPlaylists = useMemo(() => {
		if (!event?.playlists) return []
		return event.playlists
			.map((p) => {
				const fullPlaylist = allPlaylists.find((pl) => pl.id === p.id)
				if (!fullPlaylist) return null

				// IMPORTANT: preserve Playlist prototype (methods like getSmallestThumbnail)
				// Object spread would strip the prototype and turn it into a plain object.
				return Object.assign(
					Object.create(Object.getPrototypeOf(fullPlaylist)),
					fullPlaylist,
					{ percentage: p.percentage }
				) as Playlist
			})
			.filter(Boolean) as Playlist[]
	}, [event?.playlists, allPlaylists])

	const [playingPlaylistId, setPlayingPlaylistId] = useState<number | null>(
		null
	)
	const [currentSong, setCurrentSong] = useState<Song | null>(null)
	// Track song index per playlist to rotate through cached sample songs
	const [songIndexByPlaylist, setSongIndexByPlaylist] = useState<
		Record<number, number>
	>({})

	// Fetch sample for the playing playlist (cached for 5 minutes)
	const { data: sampleSongs, isLoading: isLoadingSample } = usePlaylistSample({
		playlistId: playingPlaylistId,
		config: useAccountConfig({}),
		options: {
			enabled: eventViewOpen && playingPlaylistId !== null,
		},
	})

	// Set current song when sample data is loaded, using the tracked index
	useEffect(() => {
		if (sampleSongs && sampleSongs.length > 0 && playingPlaylistId !== null) {
			const currentIndex = songIndexByPlaylist[playingPlaylistId] ?? 0
			const songIndex = currentIndex % sampleSongs.length
			setCurrentSong(sampleSongs[songIndex])
		}
	}, [sampleSongs, playingPlaylistId, songIndexByPlaylist])

	// Stop playback when modal closes
	useEffect(() => {
		if (!eventViewOpen) {
			setPlayingPlaylistId(null)
			setCurrentSong(null)
		}
	}, [eventViewOpen])

	// Get the currently playing playlist data
	const playingPlaylist = displayPlaylists.find(
		(p) => p.id === playingPlaylistId
	)

	const getPlaylistCover = (playlist: Playlist): string => {
		return playlist.getSmallestThumbnail?.() || playlist.cover || ''
	}

	// Handle play/stop button click (same behavior as PlaylistSelector)
	const handlePlayClick = (playlistId: number, e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (playingPlaylistId === playlistId) {
			// Stop playing
			setPlayingPlaylistId(null)
			setCurrentSong(null)
		} else {
			// Start playing this playlist
			// Increment the song index to pick the next song from the cached sample
			setSongIndexByPlaylist((prev) => ({
				...prev,
				[playlistId]: (prev[playlistId] ?? -1) + 1,
			}))
			setPlayingPlaylistId(playlistId)
			setCurrentSong(null) // Reset until sample loads
		}
	}

	// Handle player pause/stop
	const handlePlayerStop = () => {
		setPlayingPlaylistId(null)
		setCurrentSong(null)
	}

	const handleEditClick = () => {
		// Close the view modal first
		setEventViewOpen(false)

		// Open the appropriate edit interface based on slot type
		if (event?.slotType === 'smart' && event?.smartSlotConfig) {
			if (event.smartSlotConfig.variant === 'weather') {
				setSelectedSmartType('weather')
				setWeatherSlotStepperOpen(true)
			} else if (event.smartSlotConfig.variant === 'occupation') {
				setSelectedSmartType('occupation')
				setOccupancySlotStepperOpen(true)
			}
		} else {
			setEventEditOpen(true)
		}
	}

	const formatTime = (date?: Date) => {
		if (!date) return ''
		return DateTime.fromJSDate(date).toLocaleString(DateTime.TIME_SIMPLE)
	}

	return (
		<>
			<AlertDialog open={eventViewOpen}>
				<AlertDialogContent className="min-h-[400px]">
					<AlertDialogHeader>
						<AlertDialogTitle className="flex flex-row justify-between items-center">
							<div className="flex items-center gap-2">
								<div
									className="h-3 w-3 rounded-full border border-black/20"
									style={{
										backgroundColor:
											event?.backgroundColor || (event as any)?.color || '',
									}}
								/>
								<h1 className="text-lg font-semibold">{event?.description}</h1>
							</div>
							<div className="flex items-center gap-2">
								<span className="text-sm">
									{`${formatTime(event?.start)} - ${formatTime(event?.end)}`}
								</span>
							</div>
						</AlertDialogTitle>
						<AlertDialogDescription className="sr-only">
							{`${event?.description || event?.title || ''} ${formatTime(event?.start)} - ${formatTime(event?.end)}`.trim()}
						</AlertDialogDescription>
					</AlertDialogHeader>

					{/* Playlists Section */}
					<div className="space-y-2">
						{displayPlaylists && displayPlaylists.length > 0 ? (
							<>
								<p className="text-xs font-light text-muted-foreground">
									{t('entities.schedule.event_view.playlists_assigned')}
								</p>
								<div className="space-y-2">
									{displayPlaylists.map((playlist, index) => (
										<>
											<Link
												key={playlist.id}
												to={`/music/playlists/${playlist.id}`}
												target="_blank"
												cssVars={{
													textDecoration: 'none',
													hoverTextDecoration: 'none',
													color: 'inherit',
													hoverColor: 'inherit',
												}}
												className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent/50 transition-colors group"
											>
												{/* Cover image or Music icon */}
												<div className="relative w-16 h-16 flex-shrink-0">
													{/*
													Same cover logic as PlaylistSelector (Edit modal)
													Prefer smallest thumbnail when available.
												*/}
													{(() => {
														const cover = getPlaylistCover(playlist)
														return cover && !imageErrors[playlist.id] ? (
															<img
																src={cover}
																alt={playlist.title}
																className="w-16 h-16 rounded object-cover"
																onError={() =>
																	setImageErrors((prev) => ({
																		...prev,
																		[playlist.id]: true,
																	}))
																}
															/>
														) : (
															<div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
																<Music className="h-6 w-6 text-muted-foreground" />
															</div>
														)
													})()}

													{/* Play/Stop button overlay - always visible when playing */}
													<button
														type="button"
														onClick={(e) => handlePlayClick(playlist.id, e)}
														disabled={
															isLoadingSample &&
															playingPlaylistId === playlist.id
														}
														className={`absolute inset-0 flex items-center justify-center bg-black/50 rounded transition-opacity cursor-pointer disabled:cursor-wait ${
															playingPlaylistId === playlist.id && currentSong
																? 'opacity-100'
																: 'opacity-0 group-hover:opacity-100'
														}`}
													>
														{playingPlaylistId === playlist.id ? (
															isLoadingSample ? (
																<div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
															) : (
																<Square className="h-5 w-5 text-white fill-white" />
															)
														) : (
															<Play className="h-5 w-5 text-white fill-white" />
														)}
													</button>
												</div>

												{/* Playlist info */}
												<div className="flex-1 min-w-0">
													<p className="text-sm font-medium truncate">
														{playlist.title}
													</p>
													{/* Metadata: songs count and duration */}
													{playlist.analytics && (
														<p className="text-xs text-muted-foreground">
															{playlist.analytics.count}{' '}
															{playlist.analytics.count === 1
																? t('entities.schedule.event_view.song')
																: t('entities.schedule.event_view.songs')}{' '}
															· {formatDuration(playlist.analytics.length)}
														</p>
													)}
												</div>
											</Link>
											{/* Show percentage only if multiple playlists */}
											{playlist.percentage &&
												displayPlaylists.length > 1 &&
												index === 0 && (
													<p className="text-xs text-muted-foreground">
														{t('entities.schedule.event_view.mixed_playlists', {
															first: playlist.percentage,
															second: displayPlaylists[index + 1]?.percentage,
														})}
													</p>
												)}
										</>
									))}
								</div>
							</>
						) : event?.playlists && event.playlists.length > 0 ? (
							<p className="text-sm text-muted-foreground italic">
								{t('entities.schedule.event_view.loading_playlists')}
							</p>
						) : (
							<p className="text-sm text-muted-foreground italic">
								{t('entities.schedule.event_view.no_playlists')}
							</p>
						)}
					</div>
					<AlertDialogFooter className="items-end">
						<AlertDialogCancel
							onClick={() => setEventViewOpen(false)}
							className="rounded-full"
						>
							{t('actions.back')}
						</AlertDialogCancel>
						{!readOnly && (
							<>
								<EventDeleteForm event={event} title={event?.title} />
								<Button onClick={handleEditClick} className="rounded-full">
									{t('entities.schedule.event_view.edit')}
								</Button>
							</>
						)}
					</AlertDialogFooter>

					{/* Hidden player (we only need audio playback for preview) */}
					{currentSong && playingPlaylist && (
						<div className="mt-3 border rounded-md overflow-hidden hidden">
							<Player
								src={currentSong.path}
								cover={getPlaylistCover(playingPlaylist)}
								title={
									currentSong.metadata.title ??
									t('entities.schedule.playlist_selector.unknown_track')
								}
								radioPrevText={t('entities.schedule.playlist_selector.preview')}
								radioTitle={playingPlaylist.title}
								metadata={[
									currentSong.metadata.artist ||
										t('entities.schedule.playlist_selector.unknown_artist'),
									currentSong.metadata.album || '',
								].filter(Boolean)}
								showSkipControls={false}
								showJumpControls={false}
								autoPlay={true}
								onPause={handlePlayerStop}
								hideVolume={true}
								cssVars={{
									border: 'none',
									backgroundColor: 'transparent',
								}}
							/>
						</div>
					)}
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
